/**
 * Minimaler IndexedDB-Wrapper für den Patient-Keypair-Recovery (AFO-007).
 *
 * Bewusst ohne `idb`-Library: ein Object-Store, fester Key, keine
 * Schema-Migrationen — Version-Bumps purgen den alten Store komplett, weil
 * der Recovery-Pfad ohnehin nur 15 Minuten lebt.
 *
 * Speicherort: Datenbank `pinkCancel`, Object-Store `patientKeys`, Record-Key
 * `current`. `CryptoKey` wird via structured-clone direkt persistiert; der
 * non-extractable-Flag bleibt erhalten.
 *
 * Mit der Queue-Migration (Phase 2) sind `tenantId` und `queueItemId` Teil
 * des Records — das Recovery-Bootstrap braucht beide, um `GET /v1/pink-queue/
 * :tenantId/termin-absage/:id` ansprechen zu können.
 */

const DB_NAME = 'pinkCancel';
const STORE_NAME = 'patientKeys';
const RECORD_KEY = 'current';
const DB_VERSION = 2;
const MS_PER_MINUTE = 60_000;

/**
 * Maximale Lebensdauer eines IDB-Records ab `now`.
 *
 * 15 Minuten decken Polling-Loop (90s-Cap) + Reload-Puffer ab. Längere Werte
 * verstoßen gegen DSGVO-Datenminimierung (Security-Audit DEV-013, LOW-4).
 * AFO-009 muss diesen Wert für `expiresAt` verwenden, statt eigene Werte zu
 * setzen — `idbPutKeys` validiert das hart.
 */
export const RECOVERY_LIFETIME_MS = 15 * MS_PER_MINUTE;

/**
 * Datensatz im IDB-Store. `expiresAt` ist ein Unix-Millisekunden-Zeitstempel.
 * `tenantId` und `queueItemId` sind UUIDv4 (siehe Queue-Spec).
 */
export interface PatientKeysRecord {
  readonly tenantId: string;
  readonly queueItemId: string;
  readonly publicKeyBase64: string;
  readonly privateKey: CryptoKey;
  readonly expiresAt: number;
}

/**
 * Schreibt (oder ersetzt) den `current`-Record.
 *
 * `expiresAt` muss innerhalb von `RECOVERY_LIFETIME_MS` ab `now()` liegen —
 * andernfalls wirft die Funktion. So kann der Aufrufer nicht versehentlich
 * einen längeren Recovery-Zeitraum konfigurieren (DSGVO-Datenminimierung,
 * Security-Audit DEV-013, LOW-4).
 */
export async function idbPutKeys(record: PatientKeysRecord): Promise<void> {
  assertRecoveryWindow(record.expiresAt);
  const db = await openDatabase();
  try {
    await runRequest(
      db.transaction(STORE_NAME, 'readwrite').objectStore(STORE_NAME).put(record, RECORD_KEY),
    );
  } finally {
    db.close();
  }
}

function assertRecoveryWindow(expiresAt: number): void {
  const now = Date.now();
  const remaining = expiresAt - now;
  if (remaining > RECOVERY_LIFETIME_MS + MS_PER_MINUTE) {
    throw new Error(
      `idbPutKeys: expiresAt exceeds RECOVERY_LIFETIME_MS (got ${String(remaining)} ms remaining)`,
    );
  }
}

/**
 * Liest den `current`-Record. `null`, wenn kein Eintrag existiert oder das
 * Schema nicht passt (z. B. alter `jobId`-Record aus DB-Version 1).
 */
export async function idbGetKeys(): Promise<PatientKeysRecord | null> {
  const db = await openDatabase();
  try {
    const getRequest: IDBRequest<unknown> = db
      .transaction(STORE_NAME, 'readonly')
      .objectStore(STORE_NAME)
      .get(RECORD_KEY);
    const result = await runRequest<unknown>(getRequest);
    return isPatientKeysRecord(result) ? result : null;
  } finally {
    db.close();
  }
}

/**
 * Löscht ausschließlich den `current`-Record (Object-Store bleibt bestehen).
 */
export async function idbClear(): Promise<void> {
  const db = await openDatabase();
  try {
    await runRequest(
      db.transaction(STORE_NAME, 'readwrite').objectStore(STORE_NAME).delete(RECORD_KEY),
    );
  } finally {
    db.close();
  }
}

/**
 * Vollständige DB-Löschung — sauberster Cleanup nach Lifecycle-Ende
 * (Erfolg / Endfehler / Recovery abgelaufen).
 */
export async function idbDeleteDatabase(): Promise<void> {
  const request = indexedDB.deleteDatabase(DB_NAME);
  await runDatabaseRequest(request);
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (): void => {
      const db = request.result;
      // Bei einem Versions-Bump alten Store komplett ersetzen — Records
      // aus einer alten Schema-Version (z. B. mit `jobId` statt
      // `queueItemId`) sind nach 15 Min ohnehin abgelaufen.
      if (db.objectStoreNames.contains(STORE_NAME)) {
        db.deleteObjectStore(STORE_NAME);
      }
      db.createObjectStore(STORE_NAME);
    };
    request.onsuccess = (): void => resolve(request.result);
    request.onerror = (): void => reject(request.error ?? new Error('IDB_OPEN_FAILED'));
  });
}

function runRequest<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = (): void => resolve(request.result);
    request.onerror = (): void => reject(request.error ?? new Error('IDB_REQUEST_FAILED'));
  });
}

function runDatabaseRequest(request: IDBOpenDBRequest): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    request.onsuccess = (): void => resolve();
    request.onerror = (): void => reject(request.error ?? new Error('IDB_DELETE_FAILED'));
    request.onblocked = (): void => reject(new Error('IDB_DELETE_BLOCKED'));
  });
}

function isPatientKeysRecord(value: unknown): value is PatientKeysRecord {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const record = value as Record<string, unknown>;
  return (
    typeof record.tenantId === 'string' &&
    typeof record.queueItemId === 'string' &&
    typeof record.publicKeyBase64 === 'string' &&
    typeof record.expiresAt === 'number' &&
    isCryptoKey(record.privateKey)
  );
}

function isCryptoKey(value: unknown): value is CryptoKey {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const record = value as Record<string, unknown>;
  return (
    record.type === 'private' &&
    record.extractable === false &&
    typeof record.algorithm === 'object' &&
    Array.isArray(record.usages)
  );
}
